import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import classNamesMerger from 'classnames';

import { Icon } from 'ui';

type Props<TValue> = {
  id?: string;
  ariaLabel?: string;
  choices?: { value: TValue; label: string | React.ReactNode }[];
  placeholder?: string;
  name?: string;
  error?: string;
  minWidth?: number;
  readOnly?: boolean;
  simple?: boolean;
  classNames?: (classes: string[]) => string[];
  required?: boolean;
  separatorIndexes?: number[];
} & (
  | {
      clearable?: false;
      value: TValue;
      onChange: (newValue: TValue) => void;
      compare?: (a: TValue, b: TValue) => boolean;
    }
  | {
      clearable: true;
      value: TValue | undefined;
      onChange: (newValue: TValue | undefined) => void;
      compare?: (a: TValue, b: TValue | undefined) => boolean;
    }
);

function Select<TValue>({
  id,
  ariaLabel,
  choices,
  value,
  name,
  placeholder = gettext('Select...'),
  onChange,
  minWidth = undefined,
  compare = (a: TValue, b: TValue | undefined) => a === b,
  clearable = false,
  readOnly = false,
  error,
  simple = false,
  required = false,
  classNames = (classes) => classes,
  separatorIndexes = [],
}: Props<TValue>) {
  const valueOption = choices?.find((item) => compare(item.value, value));
  const ourChoices: { value: TValue | undefined; label: string }[] = choices || [];

  if (clearable) {
    ourChoices.push({ value: undefined, label: placeholder });
  }

  const classes = [
    'bg-white',
    'relative',
    'w-full',
    'border',
    'border-gray-300',
    'rounded-md',
    'shadow-sm',
    'pl-3',
    'pr-10',
    'py-2',
    'text-left',
    'cursor-default',
    'focus:outline-none',
    'focus:ring-1',
    'focus:ring-indigo-500',
    'focus:border-indigo-500',
    'sm:text-sm',
  ];

  if (readOnly) {
    classes.push('cursor-not-allowed');
  }

  if (error) {
    classes.push(
      'border-red-300',
      'text-red-900',
      'placeholder-red-300',
      'focus:outline-none',
      'focus:ring-red-500',
      'focus:border-red-500',
    );
  } else {
    classes.push('focus:ring-indigo-500', 'focus:border-indigo-500', 'border-gray-300');
  }

  if (simple) {
    if (!value) {
      classes.push('text-gray-500');
    }

    return (
      <select
        name={name}
        aria-label={ariaLabel}
        className={classNames(classes).join(' ')}
        style={{ minWidth }}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value || undefined);
        }}
        value={valueOption?.value || ''}
        required={required}
      >
        {!valueOption?.value && (
          <option hidden disabled value="" className="text-gray-500">
            {gettext('Select an option...')}
          </option>
        )}
        {ourChoices.map((choice, index) => (
          <>
            {separatorIndexes.includes(index) && <option disabled>―――――――</option>}
            <option key={`${choice.value}`} value={choice.value}>
              {choice.label}
            </option>
          </>
        ))}
      </select>
    );
  }

  return (
    <Listbox
      className="text-left"
      as="div"
      id={id}
      value={value}
      onChange={(newValue) => onChange(newValue)}
      disabled={readOnly}
    >
      <div className="relative mt-1">
        <Listbox.Button aria-label={ariaLabel} className={classNamesMerger(classNames(classes))} style={{ minWidth }}>
          {valueOption ? (
            <span className="block truncate text-gray-900">{valueOption.label}</span>
          ) : (
            <span className="block truncate text-gray-500">{placeholder}</span>
          )}
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <Icon name="selector" className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </Listbox.Button>

        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {ourChoices.map((option) => (
              <Listbox.Option
                key={`${option && option.value}`}
                className={({ active }) =>
                  classNamesMerger(
                    active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                    'relative cursor-default select-none py-2 pl-3 pr-9',
                  )
                }
                value={option.value}
              >
                {({ selected, active }) => (
                  <>
                    <span className={classNamesMerger(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                      {option.label}
                    </span>

                    {selected ? (
                      <span
                        className={classNamesMerger(
                          active ? 'text-white' : 'text-indigo-600',
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                        )}
                      >
                        <Icon name="check" className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}

export default Select;
